import { useTranslations } from 'next-intl';
import { type FC, useMemo } from 'react';

import { AsyncSelect, type BaseAsyncSelectProps } from '@/components/common/select/AsyncSelect';
import { type ActiveGateGroup, useActiveGateGroup } from '@/hooks/use-active-gate-group';
import { useFormatGateGroup } from '@/hooks/use-format-gate-group';

export type GateGroupOption = {
  id: string;
  name: string;
  currency_id?: string;
};

type CurrencySelectProps = BaseAsyncSelectProps<GateGroupOption> & {
  withEmpty?: boolean;
  filter?: (group: ActiveGateGroup) => boolean;
};

export const GateGroupSelect: FC<CurrencySelectProps> = (props) => {
  const t = useTranslations('misc.select.gateGroup');

  const {
    placeholder = t('placeholder'),
    emptyListNode = t('noOptionsMessage'),
    withEmpty,
    filter,
    ...selectProps
  } = props;

  const { loading, data } = useActiveGateGroup();

  const formatGateGroup = useFormatGateGroup();

  const options = useMemo(
    () =>
      [
        ...(withEmpty ? [{ id: '', name: '', currency_id: undefined }] : []),
        ...(data?.gate_group ?? []).filter((gateGroup) => !filter || filter(gateGroup)),
      ].map((gateGroup) => ({
        id: gateGroup.id,
        name: formatGateGroup(gateGroup),
        currency_id: gateGroup.currency_id,
      })),
    [data?.gate_group, filter, formatGateGroup, withEmpty]
  );

  return (
    <AsyncSelect
      loading={loading}
      options={options}
      placeholder={placeholder}
      emptyListNode={emptyListNode}
      {...selectProps}
    />
  );
};
